import axios from '@/component/axios'
import { buildAdApiPayload } from '@/utils/adCreate'

const baseApi = () => (process.env.VUE_APP_APIENDPOINT || '').trim()
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
})
const buildUrl = path => `${baseApi().replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`

const uploadHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
})

const request = (method, path, payload = {}) => {
  const normalizedMethod = String(method || 'get').toLowerCase()

  return axios({
    method: normalizedMethod,
    url: buildUrl(path),
    headers: authHeaders(),
    ...(normalizedMethod === 'get' ? { params: payload } : { data: payload }),
  })
}

async function uploadAdMediaFile(file) {
  const formData = new FormData()
  formData.append('file', file)
  const path = 'upload/media'
  const response = await axios({
    method: 'post',
    url: buildUrl(path),
    headers: uploadHeaders(),
    data: formData,
  })
  const url = response?.data?.url || response?.data?.data?.url
  if (!url) {
    throw new Error('Upload did not return a file URL')
  }
  return url
}

async function uploadAdMediaFiles(files = []) {
  const urls = []
  for (const file of files) {
    urls.push(await uploadAdMediaFile(file))
  }
  return urls
}

async function submitAdForm({ mode, form, schedule, adId, existingMediaUrls = [], clientRequestId, categories = [] }) {
  let mediaUrls = [...(existingMediaUrls || [])]
  if (Array.isArray(form.mediaFiles) && form.mediaFiles.length) {
    mediaUrls = await uploadAdMediaFiles(form.mediaFiles)
  }
  if (!mediaUrls.length) {
    throw new Error('At least one media file is required')
  }
  const payload = buildAdApiPayload({ form, schedule, mediaUrls, categories })
  if (clientRequestId) {
    payload.client_request_id = clientRequestId
  }
  if (mode === 'update') {
    return request('put', 'vendor/ads', { ...payload, ad_id: adId })
  }
  if (mode === 'submit') {
    return request('post', 'vendor/ads', payload)
  }
  return request('post', 'vendor/createNewAds', payload)
}

/** Standard vendor panel paths (legacy `vender/*` remains on API as alias). */
export const vendorPanelApi = {
  dashboard: () => request('get', 'vendor/dashboard'),
  lastUpdates: payload => request('post', 'vendor/lastUpdates', payload),
  recentFollowers: params => request('get', 'vendor/recentFollowers', params),
  bestRunningAds: () => request('get', 'vendor/bestRunningAds'),
  createNewAds: payload => request('post', 'vendor/createNewAds', payload),
  submitAdForm,
  uploadAdMediaFile,
  uploadAdMediaFiles,
  myAds: payload => request('post', 'vendor/myAds', payload),
  adsDetailById: payload => request('post', 'vendor/ads/detail', payload),
  addAds: payload => request('post', 'vendor/ads', payload),
  updateAd: payload => request('put', 'vendor/ads', payload),
  stopAd: payload => request('patch', 'vendor/ads/stop', payload),
  extendAd: payload => request('patch', 'vendor/ads/extend', payload),
  allReports: params => request('get', 'vendor/reports', params),
  allRepots: params => request('get', 'vendor/reports', params),
  reportByMetric: (metricPath, payload) => request('post', `vendor/reports/${metricPath}`, payload),
  profile: () => request('get', 'vendor/profile'),
  editProfile: payload => request('put', 'vender/editprofile', payload),
  brands: params => request('get', 'vendor/brands', params),
  createBrand: payload => request('post', 'vendor/brands', payload),
  updateBrand: (id, payload) => request('put', `vendor/brands/${id}`, payload),
  deleteBrand: id => request('delete', `vendor/brands/${id}`),
  categories: () => request('get', 'vendor/categories'),
  productCategories: () => request('get', 'vendor/product-categories'),
  syncProductCategories: categoryIds => request('put', 'vendor/product-categories', {
    category_ids: categoryIds,
  }),
  categoryRequests: () => request('get', 'vendor/category-requests'),
  requestCategory: payload => request('post', 'vendor/category-requests', payload),
  changePassword: payload => request('put', 'vendor/password', payload),
  deleteAccount: payload => request('delete', 'auth/delete-account', payload),
  walletHistory: payload => request('post', 'vendor/wallet/history', payload),
  plans: payload => request('post', 'vendor/plans', payload),
  createPlanCheckout: payload => request('post', 'vendor/plans/checkout', payload),
  confirmPlanCheckout: payload => request('post', 'vendor/plans/checkout/confirm', payload),
  upgradePlan: payload => request('post', 'vendor/plans/upgrade', payload),
  createBillingPortalSession: () => request('post', 'vendor/plans/billing-portal'),
  overallDetails: payload => request('post', 'vendor/dashboard/overall-details', payload),
  uploadedAds: payload => request('post', 'vendor/ads/uploaded', payload),
  deleteAd: payload => request('delete', 'vendor/ads', payload),
  adsReports: payload => request('post', 'vendor/ads/reports', payload),
  suspend: payload => request('post', 'vendor/users/suspend', payload),
  notifications: params => request('get', 'vendor/notifications', params),
  notificationsUnreadCount: () => request('get', 'vendor/notifications/unread-count'),
  notificationRead: id => request('patch', `vendor/notifications/${id}/read`),
  notificationsMarkAllRead: () => request('patch', 'vendor/notifications/mark-all-read'),
  registerDeviceToken: payload => request('post', 'device-token', { data: payload }),
  removeDeviceToken: payload => request('delete', 'device-token', { data: payload }),
  help: () => request('get', 'vendor/help'),
  faq: () => request('get', 'vendor/faq'),
  uploadProfileDocument: (file, documentType = 'general') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', documentType)
    return axios({
      method: 'post',
      url: buildUrl('vendor/profile/documents'),
      headers: uploadHeaders(),
      data: formData,
    })
  },
}

export const metricPathMap = {
  likes: 'likes',
  dislikes: 'dislikes',
  followers: 'followers',
  clicks: 'clicks',
  saved: 'saved',
  share: 'shares',
  reports: 'flagged',
}
